/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useAppConfig } from '@airasia-rn/ui';
import { useCallback, useRef } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';

import EnvironmentURLs, { parseEnv } from '../../util/EnvironmentURLs';
import Constants from './constants';
import {
  FlightDealsItemAPIResponse,
  FlightsDealsCarouselItemProps,
} from './types';
import { mapToVerticalContentCardProps } from './utils';

type UseFlightDealsCarouselHandlersParams = {
  recommendationItems: FlightDealsItemAPIResponse[];
  setCarouselData: React.Dispatch<
    React.SetStateAction<FlightsDealsCarouselItemProps[]>
  >;
  onItemPress: (item: FlightDealsItemAPIResponse) => void;
};

export const useFlightDealsCarouselHandlers = ({
  recommendationItems,
  setCarouselData,
  onItemPress,
}: UseFlightDealsCarouselHandlersParams) => {
  const { config } = useAppConfig();
  const env = config.env;

  const loadedCardsCount = useRef(0);
  const lastLimit = useRef(0);
  const totalCards = useRef(0);

  const handleImageError = useCallback(
    (failedItem: FlightDealsItemAPIResponse) => {
      const placeholderUri = EnvironmentURLs.FLIGHTS_PLACEHOLDER[parseEnv(env)];

      setCarouselData((prev) =>
        prev.map((card) =>
          card.model !== failedItem
            ? card
            : {
                ...card,
                image: {
                  source: {
                    uri: placeholderUri,
                  },
                },
              },
        ),
      );
    },
    [env, setCarouselData],
  );

  const initCounters = useCallback((total: number) => {
    totalCards.current = total;
    loadedCardsCount.current = Constants.INITIAL_LOAD_IMAGE_COUNT;
    lastLimit.current = 0;
  }, []);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const TOTAL_WIDTH = e.nativeEvent.contentSize.width;
      const SCROLLED_X = e.nativeEvent.contentOffset.x;
      const CARD_WIDTH = Constants.CARD_WIDTH;

      const triggerPoint = TOTAL_WIDTH - (5 * CARD_WIDTH) / 2;

      if (SCROLLED_X <= triggerPoint || lastLimit.current >= triggerPoint) {
        return;
      }

      if (loadedCardsCount.current >= totalCards.current) {
        return;
      }

      setCarouselData((prev) => [
        ...prev,
        ...mapToVerticalContentCardProps(
          recommendationItems.slice(
            loadedCardsCount.current,
            loadedCardsCount.current + Constants.SUBSEQUENT_LOAD_IMAGE_COUNT,
          ),
          handleImageError,
          onItemPress,
        ),
      ]);

      loadedCardsCount.current += Constants.SUBSEQUENT_LOAD_IMAGE_COUNT;
      lastLimit.current = triggerPoint;
    },
    [recommendationItems, handleImageError, onItemPress, setCarouselData],
  );

  return { handleImageError, handleScroll, initCounters };
};
